import type { ApprovalFlows } from '@mint-sdk/api-approval-flows';

import type {
  ApprovalSequenceType,
  EntityGroupType,
  LogicalGroupType,
  CheckFlowStatusData,
} from '../types/shared.js';

export const mapApprovalProcessToSequence = (
  processDetails: ApprovalFlows.ExecutionItem,
  currentUserId: number,
): ApprovalSequenceType => {
  return processDetails.stages.map((stage, stageIndex): LogicalGroupType => {
    const isActiveStage = stageIndex === processDetails.activeStageIndex;

    return stage.approvers.map((approver) => {
      const resolvedCandidate = approver.candidates.find(
        (candidate) => candidate.status !== 'pending',
      );
      // Only the currently active stage should surface a "pending" chip/reminder.
      // Stages before it are already resolved (approved/rejected); stages after it
      // haven't started yet, and once a stage is rejected no later stage becomes active.
      const status =
        approver.status === 'pending' && !isActiveStage
          ? null
          : approver.status;

      return {
        id: approver.id,
        status,
        resolvedAt: approver.resolvedAt,
        resolvedBy: resolvedCandidate
          ? `${resolvedCandidate.firstName} ${resolvedCandidate.lastName}`
          : null,
        items: approver.candidates.map<EntityGroupType>((candidate) => ({
          label: `${candidate.firstName} ${candidate.lastName}`,
          value: String(candidate.userId),
          isCurrentUser: candidate.userId === currentUserId,
          isRole: approver.approverType === 'role',
        })),
      };
    });
  });
};

export const resolveUserStatusFromProcessStatusData = (
  flowStatusData?: CheckFlowStatusData,
) => {
  if (!flowStatusData) {
    return 'stranger';
  }

  if (flowStatusData.userStatus === 'not_involved') {
    return flowStatusData.canCreateRequest ? 'requester' : 'stranger';
  }

  if (flowStatusData.userStatus === 'requestor') {
    return 'requester';
  }

  return flowStatusData.userStatus;
};

export const resolveProcessStatusFromProcessStatusData = (
  flowStatusData?: CheckFlowStatusData,
) => {
  if (
    !flowStatusData?.requestStatus ||
    flowStatusData.requestStatus === 'withdrawn'
  ) {
    return 'draft';
  }

  return flowStatusData.requestStatus;
};
